import { EnvelopeSimple, WhatsappLogo, Receipt, PencilSimple, Basket as BasketIcon } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { EMAIL_TEMPLATE_CATALOG, WHATSAPP_TEMPLATE_CATALOG, bookingToRow } from "@/lib/messageTemplates";

/**
 * Kompakte Icon-Aktionsleiste je Buchung.
 * - Email  → Template-Dropdown → öffnet mailto:
 * - WhatsApp → Template-Dropdown → schickt fertigen Text als E-Mail an Owner
 *   (Owner kopiert aus Postfach in seine private WhatsApp — keine Twilio-/Meta-Bürokratie)
 * - Rechnung generieren
 * - Bearbeiten (Stift)
 */
function cleanPhone(p) {
  let n = String(p || "").replace(/[^0-9]/g, "");
  if (n.startsWith("00")) n = n.slice(2);
  else if (n.startsWith("0")) n = "49" + n.slice(1);
  return n;
}

function openEmail(row, tpl) {
  const { subject, body } = tpl.build(row);
  if (!row.guest_email) {
    toast.error("Kein E-Mail-Kontakt hinterlegt.");
    return;
  }
  const url = `mailto:${encodeURIComponent(row.guest_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  toast.success(`E-Mail-Entwurf „${tpl.label}" geöffnet`);
}

async function sendWhatsAppCopy(row, tpl) {
  let body;
  if (tpl.remote) {
    try { body = (await api.get(`/bookings/${row.id}/whatsapp-confirmation-text`)).data.body; }
    catch { toast.error("Text der Buchungsbestätigung konnte nicht geladen werden"); return; }
  } else {
    body = tpl.build(row).body;
  }
  // Handynummer ist OPTIONAL — wenn vorhanden, öffnen wir zusätzlich wa.me;
  // wenn nicht, kommt trotzdem die E-Mail an Ivans Postfach.
  const num = row.guest_phone ? cleanPhone(row.guest_phone) : "";

  // 1) Text sofort in Zwischenablage kopieren (falls Browser es erlaubt)
  try {
    await navigator.clipboard.writeText(body);
  } catch { /* nicht kritisch — E-Mail hat ihn eh */ }

  // 2) E-Mail an Owner schicken (Text zum Kopieren im Postfach)
  const p = api.post("/whatsapp/copy-request", {
    booking_id: row.id,
    template_label: tpl.label,
    body,
  });

  toast.promise(p, {
    loading: `„${tpl.label}" wird an dein Postfach geschickt…`,
    success: (r) => {
      const link = r?.data?.wa_link;
      if (link) window.open(link, "_blank", "noopener,noreferrer");
      return num
        ? `📧 E-Mail an dich verschickt · WhatsApp mit ${row.first_name || "Gast"} öffnet sich`
        : `📧 E-Mail an dich verschickt · Text im Postfach zum Kopieren bereit`;
    },
    error: (e) => `Fehler: ${e?.response?.data?.detail || e?.message || "unbekannt"}`,
  });
}

function TemplateDropdown({ channel, row, disabled, icon: Icon, iconClass, testId, tooltip }) {
  const items = channel === "email" ? EMAIL_TEMPLATE_CATALOG : WHATSAPP_TEMPLATE_CATALOG;
  const handler = channel === "email" ? openEmail : sendWhatsAppCopy;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={disabled ? "Kein Kontakt hinterlegt" : tooltip}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border transition-colors ${
            disabled
              ? "opacity-40 cursor-not-allowed"
              : `hover:bg-muted cursor-pointer ${iconClass}`
          }`}
          data-testid={testId}>
          <Icon size={15} weight="bold"/>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {channel === "email" ? "E-Mail-Vorlage" : "WhatsApp-Vorlage · als E-Mail an dich"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator/>
        {items.map((tpl) => (
          <DropdownMenuItem
            key={tpl.key}
            onSelect={() => handler(row, tpl)}
            data-testid={`${testId}-tpl-${tpl.key}`}
            className="cursor-pointer flex-col items-start gap-0.5 py-2">
            <div className="font-medium text-sm">{tpl.label}</div>
            <div className="text-[11px] text-muted-foreground">{tpl.hint}</div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function BookingActionMenu({
  booking, isAdmin, onEdit, onGenInvoice, onExtras,
}) {
  const row = bookingToRow(booking);
  const hasEmail = !!row.guest_email && row.guest_email.includes("@");
  const hasPhone = !!row.guest_phone && !!cleanPhone(row.guest_phone);

  return (
    <div className="flex items-center justify-end gap-1.5" data-testid={`booking-actions-${booking.id}`}>
      <TemplateDropdown
        channel="email"
        row={row}
        disabled={!hasEmail}
        icon={EnvelopeSimple}
        iconClass="text-slate-700 hover:text-primary"
        testId={`email-menu-${booking.id}`}
        tooltip="E-Mail an Gast"/>
      <TemplateDropdown
        channel="whatsapp"
        row={row}
        disabled={false}
        icon={WhatsappLogo}
        iconClass="text-emerald-700 hover:text-emerald-800"
        testId={`whatsapp-menu-${booking.id}`}
        tooltip={hasPhone
          ? "WhatsApp-Text als E-Mail an dich + WhatsApp-Web öffnen"
          : "WhatsApp-Text als E-Mail an dich (Gast hat keine Handy-Nr)"}/>
      {isAdmin && !booking.is_block && (
        <button
          type="button"
          onClick={() => onExtras?.(booking)}
          title="Extras (Frühstück, Abendessen, Getränke, Trinkgeld)"
          className="relative inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-violet-700 hover:bg-violet-50 transition-colors"
          data-testid={`extras-btn-${booking.id}`}>
          <BasketIcon size={15} weight="bold"/>
          {(booking.extras_total || 0) > 0 && (
            <span className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center leading-none">
              {(booking.extras || []).length}
            </span>
          )}
        </button>
      )}
      {isAdmin && !booking.is_block && (
        <button
          type="button"
          onClick={() => onGenInvoice(booking.id)}
          title="Rechnung generieren"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary hover:bg-primary/10 transition-colors"
          data-testid={`gen-invoice-${booking.id}`}>
          <Receipt size={15} weight="bold"/>
        </button>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={() => onEdit(booking)}
          title="Buchung bearbeiten"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-slate-700 hover:bg-muted transition-colors"
          data-testid={`edit-booking-${booking.id}`}>
          <PencilSimple size={15} weight="bold"/>
        </button>
      )}
    </div>
  );
}
